<template>
	<div class="multiplication">
		<div class="factors-terms">{{ factors_terms.map(f => read_notation(f)).join(' x ') }} = ?</div>
		<div v-if="factors_terms.length > 2" class="multiple-multiplications">
			<multiplication :fact="factors_terms.slice(0, 2)"></multiplication>
			<multiplication :fact="[factors_terms[0] * factors_terms[1], ...factors_terms.slice(2)]"></multiplication>
		</div>
		<div v-else class="single-multiplication">
			<div v-if="isMultiplicationByTenPower" class="multiply-by-power-of-ten">
				<div class="multiply-decimal-title-power" v-if="isDecimal">
					<h4 class="title-multiply-decimal-ten-power">HOW TO MULTIPLY A DECIMAL NUMBER BY A POWER OF 10</h4>
					<ol class="steps">
						<li class="step">Count the number of zero(s) you have in the power of 10.
							<p class="step-demo">{{ tenPower }} =  1 <span class="text-danger">{{ tenPower.slice(1) }}</span></p>
						</li>
						<li class="step">Now, move the decimal point to the right the number of times determined in step 1 and place a new decimal point there. Also, fill in any empty spaces with zeros if necessary.
							<p class="step-demo">{{ otherPowerTen }} x 1<span class="text-danger">{{ tenPower.slice(1) }}</span> = <span class="text-danger">{{ result }}</span></p>
						</li>
					</ol>
				</div>
				<div class="multiply-whole-ten-power" v-else>
					<h4 class="title-multiply-by-power-of-ten">HOW DO YOU MULTIPLY WHOLE NUMBERS BY A POSITIVE POWER OF TEN?</h4>
					<ol class="steps">
						<li class="step">Count the number of zero(s) you have in the power of 10.
							<p class="step-demo">{{ tenPower }} =  1 <span class="text-danger">{{ tenPower.slice(1) }}</span></p>
						</li>
						<li class="step">Now, attach it to the right of the whole number.
							<p class="step-demo">{{ otherPowerTen }} x 1<span class="text-danger">{{ tenPower.slice(1) }}</span> = {{ otherPowerTen }}<span class="text-danger">{{ tenPower.slice(1) }}</span></p>
						</li>
					</ol>
				</div>
			</div>
			<div v-else-if="isDecimal" class="decimal-comment">
				<!-- <p>Let's multiply {{ factors_terms.map(el=>read_notation(el)).join(" x ") }}</p> -->
				<p>HOW TO MULTIPLY DECIMAL NUMBERS.</p>
				<ol class="steps">
					<li class="step">Firstly, multiply as if there is no decimal (multiply as whole numbers).
						<div class="my-3 step-demo"><multiplication :fact="[fact1, fact2]" /></div>
					</li>
					<li class="step">Secondly, count the number of digits after the decimal in each given factor.
						<div class="step-demo"><component :is="countDecimalLengths" /></div>
					</li>
					<li class="step">Finally, put the same number of digits behind the decimal in the product ( i.e count from the right to the left {{ decimalLength }} places).
						<p class="ml-3 step-demo">
							<span :is="decimal_expression"></span>
							<equal-sign />
							<span :is="result_decimal"></span>
						</p>
					</li>
				</ol>
			</div>
			<div v-else-if="withZero" class="ending-zero-comment">
				<h4 class="ending-zero-title">HOW DO YOU MULTIPLY NUMBERS ENDING IN ZEROS?</h4>
				<ol class="steps">
					<li class="step">Multiply the numbers as if it didn’t have zeros.
						<div class="pl-5 step-demo"><multiplication :fact="[fact1, fact2]" /></div>
					</li>
					<li class="step">Now, add all the zeros the number(s) had to the product.
						<p class="step-demo">
							{{ read_notation(fact1) }}<span class="text-danger">{{ zeros1 }}</span> x
							{{ read_notation(fact2) }}<span class="text-danger">{{ zeros2 }}</span> =
							<component :is="decimal_with_zeroes"></component>
						</p>
					</li>
				</ol>
			</div>
			<div class="main-operation" v-else>
				<div class="switch-method-div border p-1 border-danger">
					<h6 class="switch-method-title">Multiplication method</h6>
					<div class="btns-switch btn-group">
						<button
						v-for="(button, i) in multiplicationMethods"
						:class="['btn-switch-method btn', method.component == button.component ? 'btn-info active' : 'btn-light']"
						@click = "method = button"
						:key="`btn-${i}`">
						<span class="btn-text">{{ button.title }}</span>
						</button>
					</div>
				</div>
				<transition name="slideDown" appear mode="out-in">
					<component class="multiplication-method" :key="method.component" :is="method.component" :fact="[fact1, fact2]"></component>
				</transition>
			</div>
		</div>
	</div>
</template>

<script type="text/javascript">
	const $operations = require("../scripts/variables").operations;
	import BoxMultiplication from './BoxMultiplication.vue';
	import SimpleMultiplication from './SimpleMultiplication.vue';
	import EqualSign from '../EqualSign.vue';
	import BaseExerciseMixin from '../../js/BaseExerciseMixin';
	import UFCT from 'usefull-functions';

	const multiplicationMethods = [
		{
			title: 'Box multiplication',
			method: 'box',
			component: 'box-multiplication'
		},
		{
			title: 'Simple multiplication',
			component: 'simple-multiplication'
		}
	];

	export default {
		mixins: [BaseExerciseMixin],
		name: 'multiplication',
		components: { BoxMultiplication, SimpleMultiplication, EqualSign },
		props: {
			fact: {type: Array, required: true, default: () => ([])},
			deepth: {type: Number, required: false, default: 0}
		},
		data(){
			return {
				withZero: false,
				fact1: Number,
				fact2: Number,
				isDecimal: this.fact.map(el => parseInt(el) !== Number(el)).reduce((a, b) => a || b, false),
				zeros1: '',
				zeros2: '',
				result: $operations.multiplication(this.fact),
				tenPower: 0,
				factors_terms: this.fact,
				method: 'simple',
				multiplicationMethods: multiplicationMethods,
				decimalLength: 0
			}
		},
		created(){
			this.fact1 = this.factors_terms[0];
			this.fact2 = this.factors_terms[1];
			let nbre_zero = 0, fact1 = this.fact1.toString().replace('.', ''), fact2 = this.fact2.toString().replace('.', '');

			while (fact1 % 10 == 0) {
				this.withZero = true;
				this.zeros1 = this.zeros1.concat('0');
				this.nbre_zero++;
				fact1 = fact1 / 10;
			}
			if(fact1 == 1){
				this.tenPower = this.fact1.toString();
				this.otherPowerTen = this.fact2;
			};
			while (fact2 % 10 == 0) {
				this.withZero = true;
				this.zeros2 = this.zeros2.concat('0');
				this.nbre_zero++;
				fact2 = fact2 / 10;
			}
			if(fact2 == 1){
				this.tenPower = this.fact2.toString();
				this.otherPowerTen = this.fact1;
			};
			this.fact1 = fact1;
			this.fact2 = fact2;
			if (this.isMultiplicationComponent) {
				this.method = multiplicationMethods.filter(method => method.component == 'simple-multiplication').pop();
			} else {
				this.method = multiplicationMethods.filter(method => method.component != 'simple-multiplication').pop();
			}
			if (this.isDecimal) {
				this.decimalLength = this.result.toString().split('.')[1].toString().length;
			}
		},
		methods: {
		},
		computed: {
			decimal_with_zeroes() {
				let str = "";
				if(!this.isDecimal){
					str = (this.fact1 * this.fact2) + this.tag('span', this.zeros1 + '' + this.zeros2, {class: 'text-danger'});
				}else{
					str = parseInt(this.result) == Number(this.result) ? this.result + this.tag('span', '.' + this.zeros1 + '' + this.zeros2, { class: 'text-danger' }):
						this.result.toString().split('.').join(this.tag('span', '.', {class: 'text-danger'}));
				}
				return {template: this.tag('span', str)}
			},
			decimal_expression() {
				let str = this.factors_terms.map(el => {
					let n = this.read_notation(el).toString().split('.');
					return n.length > 1 ? (n[0] + '.' + this.tag('span', n[1], {class: 'text-danger'})) : n[0]
				}).join(' x ');
				return {template: this.tag('span', str)};
			},
			result_decimal(){

				let parts = this.result.toString().split('.'),
					str = parts[0] + this.tag('span','.', {class:'text-danger'}) + this.tag('span', parts[1], {class: 'text-info'});
				return {template: this.tag('span', str)};
			},
			isMultiplicationComponent(){
				let tab = [this.fact1, this.fact2];
				return tab.map(el => el < 11 || el > 99999).reduce((a, b) => a || b, false);
			},
			isMultiplicationByTenPower(){
				return this.withZero && (this.fact1 == 1 || this.fact2 == 1);
			},
			countDecimalLengths() {
				let terms = this.factors_terms.map(term => {
					let n = term.toString().split('.'),
						decimal = n.length > 1,
						bloc = UFCT.tag('span',
							UFCT.tag('div',
								`(${UFCT.tag('span', decimal ? n[1].toString().length : '0', {class: 'text-danger decimal-length'})})`,
								{class: "term-decimal-length"}
							)
							+ UFCT.tag('div',
								n[0] + (decimal ? ('.' + this.tag('span', n[1], {class: 'text-danger term-decimal-part'})) : ''),
								{ class: 'term-illustrate-decimal-part'}
							),
							{class: 'term-count-decimal-length'}
						)
					return bloc;
				}).join('');
				return {
					template: UFCT.tag('div', terms, {class: 'terms-count-decimal-length'}),
					data() {
						return {}
					}
				}
			}
		}
	};
</script>
<style lang="scss">
	.multiplication {
		font-size: 0.75em;
		.factor-terms {}
		.multiple-multiplications {
			.single-multiplication {}
		}
		.single-multiplication {
			.steps {
				.step {
					list-style-image: none;
					list-style-type: disc;
					.step-demo {
						// font-family: 'Roboto Mono', monospace;
					}
				}
			}
			.multiply-by-power-of-ten {
				font-size: 20px;
				.title-multiply-by-power-of-ten {
					margin-top: 1rem;
					text-align: center;
				}
			}
			.decimal-comment {
			}
			.ending-zero-comment {
				.ending-zero-title {
					margin-top: 1.5rem;
					text-align: center;
				}
			}
			.main-operation {
				.switch-method-div {
					display: flex;
					align-items: center;
					justify-content: space-between;
					.switch-method-title {}
					.btns-switch {
						.btn-switch-method {
							&.active {}
						}
					}
				}
			}
		}
		.terms-count-decimal-length {
			line-height: 1;
			display: grid;
			grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
			margin: 2rem auto;
			gap: 1rem;
			.term-count-decimal-length {
				text-align: right;
				.term-decimal-length {
					font-size: 0.7em;
					.decimal-length {}
				}
			}
		}
	}
</style>